import type { Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { commit, newId, type Writer } from '../db/commit';
import { getDeviceId } from '../db/clock';
import { db } from '../db/db';
import type { ChainRow, StoreRow } from '../db/types';
import { geoSearch } from '../lib/geo';
import { forgetPlacementTries } from './placementTries';
import { buildMergeMap, resolveWith } from './products';

export interface StoreView extends StoreRow {
  chainName: string | null;
  /** Początek ostatniej wizyty w sklepie (do sortowania „ostatnio odwiedzane”). */
  lastVisitMs: number | null;
}

/**
 * Scalone sklepy (ten sam sklep założony na dwóch telefonach przed dołączeniem do wspólnego domu):
 * duplikat ma mergedInto, a wszystko, co na niego wskazuje, traktujemy jak sklep, który został.
 */
export async function loadStoreResolver(): Promise<(id: Id) => Id> {
  return resolveWith(buildMergeMap(await db.stores.toArray()));
}

/** Id sklepu po scaleniach (undefined = jeszcze ładujemy). */
export function useStoreResolver(): ((id: Id) => Id) | undefined {
  return useLiveQuery(loadStoreResolver);
}

interface StoreIndex {
  stores: StoreView[];
  resolve: (id: Id) => Id;
}

function useStoreIndex(): StoreIndex | undefined {
  return useLiveQuery(async () => {
    const [stores, chains, visits] = await Promise.all([db.stores.toArray(), db.chains.toArray(), db.visits.toArray()]);
    const resolve = resolveWith(buildMergeMap(stores));
    const resolveChain = resolveWith(buildMergeMap(chains));
    const chainName = new Map(chains.map((c) => [c.id, c.name]));
    const last = new Map<Id, number>();
    for (const v of visits) {
      const id = resolve(v.storeId);
      last.set(id, Math.max(last.get(id) ?? 0, v.startedMs));
    }
    const list = stores
      .filter((s) => !s.deleted && !s.mergedInto)
      .map((s) => ({
        ...s,
        chainName: s.chainId ? (chainName.get(resolveChain(s.chainId)) ?? null) : null,
        lastVisitMs: last.get(s.id) ?? null,
      }))
      .sort(
        (a, b) =>
          Number(!!b.favorite) - Number(!!a.favorite) ||
          (b.lastVisitMs ?? 0) - (a.lastVisitMs ?? 0) ||
          (a.name ?? '').localeCompare(b.name ?? '', 'pl'),
      );
    return { stores: list, resolve };
  });
}

/** Sklep sieci Lidl (po nazwie sieci albo sklepu) — paragony i ceny są z Lidla. */
export const isLidl = (s: { name?: string | null; chainName?: string | null }) => `${s.chainName ?? ''} ${s.name ?? ''}`.toLowerCase().includes('lidl');

/** Sklepy: ulubione na górze, potem ostatnio odwiedzane, potem alfabetycznie. Bez scalonych duplikatów. */
export function useStores(): StoreView[] | undefined {
  return useStoreIndex()?.stores;
}

/** Sklep po id; id scalonego duplikatu daje sklep, który został. */
export function useStore(id: Id | null | undefined): StoreView | null | undefined {
  const index = useStoreIndex();
  if (index === undefined) return undefined;
  if (!id) return null;
  const target = index.resolve(id);
  return index.stores.find((s) => s.id === target) ?? null;
}

export function useChains(): ChainRow[] | undefined {
  return useLiveQuery(async () =>
    (await db.chains.toArray())
      .filter((c) => !c.deleted && !c.mergedInto)
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'pl')),
  );
}

/** Znajduje sieć po nazwie (bez względu na wielkość liter) albo ją tworzy. */
async function chainIdFor(w: Writer, chainName: string | null | undefined): Promise<Id | null> {
  const name = chainName?.trim();
  if (!name) return null;
  const chains = (await db.chains.toArray()).filter((c) => !c.deleted && c.name?.toLowerCase() === name.toLowerCase());
  const existing = chains.find((c) => !c.mergedInto) ?? chains[0];
  if (existing) return resolveWith(buildMergeMap(await db.chains.toArray()))(existing.id);
  const id = newId();
  await w.set('chains', id, { name });
  return id;
}

/** Współrzędne z „Adres stąd” (pozycja telefonu w sklepie) i adres znaleziony w tym miejscu. */
interface HereInput {
  lat?: number | null;
  lng?: number | null;
  geoLabel?: string | null;
}

export async function createStore(
  input: { name: string; chainName?: string | null; address?: string | null; favorite?: boolean } & HereInput,
): Promise<Id> {
  const id = newId();
  const address = input.address?.trim() || null;
  const here = hereOf(input);
  await commit(async (w) => {
    const chainId = await chainIdFor(w, input.chainName);
    // Pierwszy sklep od razu jako ulubiony; kolejne tylko na życzenie.
    const isFirst = (await db.stores.filter((s) => !s.deleted && !s.mergedInto).count()) === 0;
    await w.set('stores', id, {
      name: input.name.trim(),
      chainId,
      address,
      favorite: input.favorite ?? isFirst,
      ...(here ? { lat: here.lat, lng: here.lng, geoSource: 'here' as const } : {}),
    });
    if (here) await putGeoLabel(id, here);
  });
  // Zapis lokalny nie czeka na sieć — położenie z adresu dojdzie w tle.
  if (address && !here) void geocodeStore(id).catch(() => {});
  return id;
}

export async function updateStore(
  id: Id,
  input: { name?: string; chainName?: string | null; address?: string | null; favorite?: boolean } & HereInput,
): Promise<void> {
  const here = hereOf(input);
  let addressChanged = false;
  await commit(async (w) => {
    const fields: Partial<StoreRow> = {};
    if (input.name !== undefined) fields.name = input.name.trim();
    if (input.address !== undefined) {
      fields.address = input.address?.trim() || null;
      addressChanged = ((await db.stores.get(id))?.address ?? null) !== fields.address;
    }
    if (input.favorite !== undefined) fields.favorite = input.favorite;
    if (input.chainName !== undefined) fields.chainId = await chainIdFor(w, input.chainName);
    // Położenie należy do adresu: nowy adres = stare współrzędne (i ich odrzucenie) znikają w tym samym zapisie.
    if (here) {
      fields.lat = here.lat;
      fields.lng = here.lng;
      fields.geoSource = 'here';
      await putGeoLabel(id, here);
    } else if (addressChanged) {
      fields.lat = null;
      fields.lng = null;
      fields.geoSource = null;
    }
    await w.set('stores', id, fields);
  });
  if (addressChanged && !here) void geocodeStore(id).catch(() => {});
}

function hereOf(input: HereInput): { lat: number; lng: number; label: string | null } | null {
  return input.lat != null && input.lng != null ? { lat: input.lat, lng: input.lng, label: input.geoLabel ?? null } : null;
}

export async function deleteStore(id: Id): Promise<void> {
  await commit(async (w) => {
    await w.set('stores', id, { deleted: true });
    // Niezakończone listy tracą sklep (także te wskazujące na scalone z nim duplikaty) — przy zakupach aplikacja poprosi o wybór nowego.
    const resolve = await loadStoreResolver();
    const lists = await db.lists.where('status').equals('active').toArray();
    for (const l of lists) if (!l.deleted && l.storeId && resolve(l.storeId) === id) await w.set('lists', l.id, { storeId: null });
  });
}

/**
 * Łączy duplikat sklepu z innym („Połącz z innym sklepem…”). Nic nie przepisujemy: duplikat dostaje mergedInto,
 * a listy, wizyty i dziennik kolejności obu sklepów składają się w jedną trasę przy odczycie.
 */
export async function mergeStoreInto(dupId: Id, survivorId: Id): Promise<void> {
  await commit(async (w) => {
    const all = await db.stores.toArray();
    const resolve = resolveWith(buildMergeMap(all));
    const from = resolve(dupId);
    const into = resolve(survivorId);
    if (from === into) return;
    const dup = all.find((s) => s.id === from);
    const survivor = all.find((s) => s.id === into);
    if (!dup || !survivor || dup.deleted || survivor.deleted) throw new Error('Nie ma takiego sklepu');
    await w.set('stores', from, { mergedInto: into });
    // Sklep, który zostaje, przejmuje to, czego sam nie ma.
    const fill: Partial<StoreRow> = {};
    if (dup.favorite && !survivor.favorite) fill.favorite = true;
    // Współrzędne idą razem z adresem, z którego pochodzą (i z tym, skąd są); odrzuconego położenia nie przywracamy.
    const takesAddress = !!dup.address && !survivor.address;
    if (takesAddress) fill.address = dup.address;
    const coordsFit = takesAddress || (dup.address === survivor.address && survivor.geoSource !== 'rejected');
    if (dup.lat != null && dup.lng != null && survivor.lat == null && coordsFit) {
      fill.lat = dup.lat;
      fill.lng = dup.lng;
      fill.geoSource = dup.geoSource ?? null;
    } else if (takesAddress && dup.geoSource === 'rejected' && survivor.lat == null) {
      // Odrzucone położenie idzie razem z adresem — inaczej inny telefon zgeokodowałby go ponownie.
      fill.geoSource = 'rejected';
    }
    if (dup.chainId && !survivor.chainId) fill.chainId = dup.chainId;
    if (Object.keys(fill).length > 0) await w.set('stores', into, fill);
  });
}

/** Zapomina nauczoną kolejność sklepu (np. po dużej przebudowie). */
export async function resetStoreOrder(storeId: Id): Promise<void> {
  const resolve = await loadStoreResolver();
  await commit(async (w) => {
    await w.append({ storeId: resolve(storeId), kind: 'reset', deviceId: getDeviceId() });
  });
  await forgetPlacementTries(resolve(storeId));
}

// --- Położenie sklepu z adresu (geokodowanie przez serwer, dane © OpenStreetMap) ---

/**
 * Opis miejsca od geokodera (meta, bez synchronizacji) — tylko do wyświetlenia na tym telefonie.
 * Stan położenia (z adresu / potwierdzone / odrzucone) synchronizuje się polem stores.geoSource.
 */
interface GeoLabel {
  label: string | null;
  lat: number;
  lng: number;
}

const GEO_LABEL = 'storeGeo:';
const BACKFILL_MAX = 5;

async function putGeoLabel(storeId: Id, label: GeoLabel): Promise<void> {
  await db.meta.put({ key: GEO_LABEL + storeId, value: { label: label.label, lat: label.lat, lng: label.lng } });
}

/** Opisy miejsc od geokodera (klucz: id sklepu). */
export function useStoreGeoLabels(): Map<Id, GeoLabel> | undefined {
  return useLiveQuery(async () => {
    const rows = await db.meta.where('key').startsWith(GEO_LABEL).toArray();
    return new Map(rows.map((r) => [r.key.slice(GEO_LABEL.length), r.value as GeoLabel]));
  });
}

/** Opis, o ile dotyczy obecnych współrzędnych sklepu (inny telefon mógł je w międzyczasie zmienić). */
export function geoLabelFor(store: StoreRow, label: GeoLabel | undefined): string | null {
  if (!label?.label || store.lat == null || store.lng == null) return null;
  return Math.abs(label.lat - store.lat) < 1e-6 && Math.abs(label.lng - store.lng) < 1e-6 ? label.label : null;
}

/** „To tu” — położenie z adresu potwierdzone (na wszystkich telefonach). */
export async function confirmStoreGeo(storeId: Id): Promise<void> {
  await commit((w) => w.set('stores', storeId, { geoSource: 'here' }));
}

/** „To nie tu” — czyści współrzędne; żaden telefon nie geokoduje już tego adresu, dopóki się nie zmieni. */
export async function rejectStoreGeo(storeId: Id): Promise<void> {
  await commit(async (w) => {
    await w.set('stores', storeId, { lat: null, lng: null, geoSource: 'rejected' });
    await db.meta.delete(GEO_LABEL + storeId);
  });
}

/** Sklepy już geokodowane w tej sesji (także bez wyniku) — nie pytamy o nie drugi raz. */
const geoTried = new Set<Id>();

/**
 * Współrzędne sklepu z jego adresu. Najpierw „sieć + adres” (trafia w sam sklep, gdy jest w OSM),
 * potem sam adres. Zapisuje tylko wtedy, gdy adres się w międzyczasie nie zmienił i sklep nadal nie ma położenia.
 */
export async function geocodeStore(storeId: Id): Promise<boolean> {
  if (!navigator.onLine) return false;
  const store = await db.stores.get(storeId);
  const address = store?.address?.trim();
  if (!store || store.deleted || store.mergedInto || !address || store.lat != null || store.geoSource === 'rejected') return false;
  geoTried.add(storeId);
  const chainName = store.chainId ? await chainNameOf(store.chainId) : null;
  const hit = (chainName ? await geoSearch(`${chainName} ${address}`) : null) ?? (await geoSearch(address));
  if (!hit) return false;
  return commit(async (w) => {
    const current = await db.stores.get(storeId);
    if (!current || current.address?.trim() !== address || current.lat != null || current.geoSource === 'rejected') return false;
    await w.set('stores', storeId, { lat: hit.lat, lng: hit.lng, geoSource: 'address' });
    await putGeoLabel(storeId, hit);
    return true;
  });
}

async function chainNameOf(chainId: Id): Promise<string | null> {
  const chains = await db.chains.toArray();
  const target = resolveWith(buildMergeMap(chains))(chainId);
  return chains.find((c) => c.id === target)?.name?.trim() || null;
}

let backfilling = false;

/** Uzupełnia położenie sklepów z adresem bez współrzędnych (najwyżej 5 na wywołanie, po kolei). */
export async function backfillStoreGeo(): Promise<void> {
  if (backfilling || !navigator.onLine) return;
  backfilling = true;
  try {
    const todo = (await db.stores.toArray())
      .filter((s) => !s.deleted && !s.mergedInto && s.lat == null && s.geoSource !== 'rejected' && !!s.address?.trim() && !geoTried.has(s.id))
      .slice(0, BACKFILL_MAX);
    for (const s of todo) await geocodeStore(s.id);
  } finally {
    backfilling = false;
  }
}
